import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Icon,
  Stack,
  Text,
  SimpleGrid,
} from '@chakra-ui/react';
import { FaRocket, FaChartLine, FaUserCog, FaGift } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import LanguageSelector from './components/LanguageSelector';

const MotionBox = motion(Box);

const Feature = ({ title, text, icon }) => {
  return (
    <Stack
      bg="gray.800"
      borderRadius="xl"
      p={8}
      border="1px"
      borderColor="blue.500"
      _hover={{
        transform: 'translateY(-5px)',
        boxShadow: '0 0 20px rgba(66, 153, 225, 0.3)',
      }}
      transition="all 0.3s"
    >
      <Flex
        w={16}
        h={16}
        align={'center'}
        justify={'center'}
        color={'white'}
        rounded={'full'}
        bg={'gray.700'}
        mb={1}
      >
        {icon}
      </Flex>
      <Text fontWeight={600} color="white">
        {title}
      </Text>
      <Text color={'gray.400'} fontSize={'sm'}>
        {text}
      </Text>
    </Stack>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const features = [
    {
      icon: <Icon as={FaRocket} w={10} h={10} />,
      title: t('landing.features.instantResults.title'),
      text: t('landing.features.instantResults.description'),
    },
    {
      icon: <Icon as={FaChartLine} w={10} h={10} />,
      title: t('landing.features.fairPayouts.title'),
      text: t('landing.features.fairPayouts.description'),
    },
    {
      icon: <Icon as={FaUserCog} w={10} h={10} />,
      title: t('landing.features.customizableWins.title'),
      text: t('landing.features.customizableWins.description'),
    },
    {
      icon: <Icon as={FaGift} w={10} h={10} />,
      title: t('landing.features.profitSharing.title'),
      text: t('landing.features.profitSharing.description'),
    },
  ];

  return (
    <Box bg="gray.900" minH="100vh" overflowX="hidden">
      {/* Hero Section */}
      <Container maxW={'7xl'} px={{ base: 4, md: 8 }}>
        <Box position="absolute" top={4} right={4}>
          <LanguageSelector />
        </Box>
        <Stack
          align={'center'}
          spacing={{ base: 8, md: 10 }}
          py={{ base: 12, md: 28 }}
          direction={{ base: 'column', md: 'row' }}
        >
          <Stack flex={1} spacing={{ base: 4, md: 10 }} textAlign={{ base: 'center', md: 'left' }}>
            <Heading
              lineHeight={1.1}
              fontWeight={600}
              fontSize={{ base: '2xl', sm: '3xl', lg: '6xl' }}
            >
              <Text
                as={'span'}
                position={'relative'}
                color={'white'}
                _after={{
                  content: "''",
                  width: 'full',
                  height: '30%',
                  position: 'absolute',
                  bottom: 1,
                  left: 0,
                  bg: 'blue.400',
                  zIndex: -1,
                  display: { base: 'none', md: 'block' }
                }}
              >
                {t('landing.title')}
              </Text>
              <br />
              <Text as={'span'} bgGradient="linear(to-r, blue.400,purple.400)" bgClip="text">
                {t('landing.subtitle')}
              </Text>
            </Heading>
            <Text color={'gray.300'} fontSize={{ base: 'sm', md: 'md', lg: 'lg' }} px={{ base: 2, md: 0 }}>
              {t('landing.description')}
            </Text>
            <Stack 
              spacing={{ base: 4, sm: 6 }} 
              direction={{ base: 'column', sm: 'row' }}
              justify={{ base: 'center', md: 'flex-start' }}
              w="100%"
            >
              <Button
                rounded={'full'}
                size={{ base: 'md', md: 'lg' }}
                fontWeight={'bold'}
                px={{ base: 4, md: 6 }}
                colorScheme={'blue'}
                bg={'blue.400'}
                _hover={{ bg: 'blue.500' }}
                onClick={() => navigate('/app')}
              >
                {t('landing.getStarted')}
              </Button>
              <Button
                rounded={'full'}
                size={{ base: 'md', md: 'lg' }}
                fontWeight={'normal'}
                px={{ base: 4, md: 6 }}
                w={{ base: 'full', sm: 'auto' }}
                leftIcon={<FaRocket />}
                colorScheme="purple"
                variant="outline"
                onClick={() => navigate('/doc')}
              >
                {t('landing.learnMore')}
              </Button>
            </Stack>
          </Stack>
          <MotionBox
            flex={1}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            w={{ base: 'full', md: '50%' }}
            mt={{ base: 8, md: 0 }}
          >
            <Box
              position={'relative'}
              height={{ base: '200px', md: '400px' }}
              rounded={'2xl'}
              boxShadow={'2xl'}
              width={'full'}
              overflow={'hidden'}
              bg="blue.900"
              bgGradient="linear(to-r, blue.900, purple.900)"
              border="1px"
              borderColor="blue.500"
            >
              <img
                src="/banner.png"
                alt="Banner"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
            </Box>
          </MotionBox>
        </Stack>

        {/* Features Section */}
        <Box py={{ base: 12, md: 20 }}>
          <Heading
            textAlign="center"
            mb={{ base: 8, md: 16 }}
            fontSize={{ base: '2xl', md: '4xl' }}
            fontWeight="bold"
            color="white"
            px={{ base: 4, md: 0 }}
          >
            {t('landing.features.title')}
          </Heading>
          <SimpleGrid 
            columns={{ base: 1, md: 2, lg: 4 }} 
            spacing={{ base: 6, md: 10 }}
            px={{ base: 4, md: 0 }}
          >
            {features.map((feature, index) => (
              <Feature
                key={index}
                icon={feature.icon}
                title={feature.title}
                text={feature.text}
              />
            ))}
          </SimpleGrid>
        </Box>

        {/* Stats Section */}
        <Box py={{ base: 16, md: 24 }}>
          <SimpleGrid 
            columns={{ base: 1, md: 3 }} 
            spacing={{ base: 8, md: 12 }}
            px={{ base: 6, md: 8 }}
            maxW="7xl"
            mx="auto"
          >
            <Box
              bg="gray.800"
              p={{ base: 8, md: 10 }}
              borderRadius="2xl"
              textAlign="center"
              border="1px"
              borderColor="blue.500"
              boxShadow="lg"
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-4px)', boxShadow: 'xl' }}
            >
              <Text fontSize={{ base: '4xl', md: '5xl' }} fontWeight="bold" color="blue.400" mb={2}>
                100%
              </Text>
              <Text color="gray.300" fontSize={{ base: 'md', md: 'lg' }}>{t('landing.stats.instantPayouts')}</Text>
            </Box>
            <Box
              bg="gray.800"
              p={{ base: 8, md: 10 }}
              borderRadius="2xl"
              textAlign="center"
              border="1px"
              borderColor="purple.500"
              boxShadow="lg"
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-4px)', boxShadow: 'xl' }}
            >
              <Text fontSize={{ base: '4xl', md: '5xl' }} fontWeight="bold" color="purple.400" mb={2}>
                10000x
              </Text>
              <Text color="gray.300" fontSize={{ base: 'md', md: 'lg' }}>{t('landing.stats.maxLeverage')}</Text>
            </Box>
            <Box
              bg="gray.800"
              p={{ base: 8, md: 10 }}
              borderRadius="2xl"
              textAlign="center"
              border="1px"
              borderColor="blue.500"
              boxShadow="lg"
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-4px)', boxShadow: 'xl' }}
            >
              <Text fontSize={{ base: '4xl', md: '5xl' }} fontWeight="bold" color="blue.400" mb={2}>
                10%
              </Text>
              <Text color="gray.300" fontSize={{ base: 'md', md: 'lg' }}>{t('landing.stats.profitSharing')}</Text>
            </Box>
          </SimpleGrid>
        </Box>

        {/* CTA Section */}
        <Box py={{ base: 16, md: 24 }} px={{ base: 6, md: 8 }}>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            spacing={{ base: 8, md: 12 }}
            align={'center'}
            justify={'space-between'}
            bg="gray.800"
            p={{ base: 8, md: 12 }}
            borderRadius="2xl"
            border="1px"
            borderColor="blue.500"
            boxShadow="lg"
            maxW="7xl"
            mx="auto"
          >
            <Stack flex={1} spacing={6} maxW={{ md: '60%' }}>
              <Heading color="white" fontSize={{ base: '3xl', md: '4xl' }} lineHeight="shorter">
                {t('landing.cta.title')}
              </Heading>
              <Text color="gray.300" fontSize={{ base: 'md', md: 'lg' }} lineHeight="tall">
                {t('landing.cta.description')}
              </Text>
            </Stack>
            <Stack 
              spacing={4}
              direction={{ base: 'column', sm: 'row' }}
              align="center"
              w={{ base: 'full', md: 'auto' }}
            >
              <Button
                rounded={'full'}
                size={'lg'}
                fontWeight={'bold'}
                px={8}
                colorScheme={'blue'}
                bg={'blue.400'}
                _hover={{ bg: 'blue.500', transform: 'translateY(-2px)' }}
                transition="all 0.3s"
                onClick={() => navigate('/app')}
              >
                {t('landing.cta.playNow')}
              </Button>
              <Button
                rounded={'full'}
                size={'lg'}
                fontWeight={'normal'}
                px={8}
                variant="outline"
                colorScheme="purple"
                leftIcon={<FaRocket />}
                _hover={{ bg: 'whiteAlpha.200', transform: 'translateY(-2px)' }}
                transition="all 0.3s"
                onClick={() => navigate('/doc')}
              >
                {t('landing.cta.learnMore')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
